import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useSpring, useTransform } from 'motion/react';
import { twMerge } from 'tailwind-merge';
import { Check, ChevronDown } from 'lucide-react';

// ─── Context ────────────────────────────────────────────────────────────────

const SelectCtx = createContext(null);
const useSelect = () => useContext(SelectCtx);

// ─── Proximity Hover Hook ───────────────────────────────────────────────────

function useProximityHover(ref, radius = 120) {
  const [isNear, setIsNear] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handleMouseMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const near = dist < radius;
      setIsNear(near);
      if (near) {
        setPosition({
          x: dx / (rect.width / 2),
          y: dy / (rect.height / 2),
        });
      }
    };

    const handleMouseLeave = () => {
      setIsNear(false);
      setPosition({ x: 0, y: 0 });
    };

    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [ref, radius]);

  return { isNear, position };
}

// ─── Select Root ────────────────────────────────────────────────────────────

export function Select({ value, onValueChange, children }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);

  const toggle = useCallback(() => setOpen((v) => !v), []);
  const close = useCallback(() => setOpen(false), []);
  const select = useCallback(
    (val) => {
      onValueChange?.(val);
      close();
    },
    [onValueChange, close]
  );

  // Close on outside click — exclude both trigger and portal content
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const inTrigger = triggerRef.current?.contains(e.target);
      const inContent = contentRef.current?.contains(e.target);
      if (!inTrigger && !inContent) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, close]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, close]);

  return (
    <SelectCtx.Provider value={{ value, open, toggle, close, select, triggerRef, contentRef }}>
      {children}
    </SelectCtx.Provider>
  );
}

// ─── Trigger ────────────────────────────────────────────────────────────────

export function SelectTrigger({ className, placeholder = 'Select…' }) {
  const { value, open, toggle, triggerRef } = useSelect();
  const { isNear, position } = useProximityHover(triggerRef);

  const springX = useSpring(0, { stiffness: 300, damping: 30 });
  const springY = useSpring(0, { stiffness: 300, damping: 30 });
  const rotateX = useTransform(springY, [-1, 1], [4, -4]);
  const rotateY = useTransform(springX, [-1, 1], [-4, 4]);

  useEffect(() => {
    if (isNear) {
      springX.set(position.x);
      springY.set(position.y);
    } else {
      springX.set(0);
      springY.set(0);
    }
  }, [isNear, position, springX, springY]);

  return (
    <motion.button
      ref={triggerRef}
      type="button"
      onClick={toggle}
      style={{ rotateX, rotateY, transformPerspective: 800 }}
      whileTap={{ scale: 0.97 }}
      className={twMerge(
        'relative flex items-center justify-between gap-2',
        'min-w-[220px] w-full px-3.5 py-2 rounded-xl',
        'bg-white/5 border border-white/10',
        'text-slate-100 text-sm font-medium',
        'cursor-pointer select-none outline-none',
        'transition-colors duration-200',
        open
          ? 'border-white/25 bg-white/8'
          : 'hover:border-white/20 hover:bg-white/7',
        className
      )}
      aria-haspopup="listbox"
      aria-expanded={open}
    >
      <span className={value ? 'text-slate-100' : 'text-slate-400'}>
        {value || placeholder}
      </span>
      <motion.span
        animate={{ rotate: open ? 180 : 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className="text-slate-400 flex-shrink-0"
      >
        <ChevronDown size={14} />
      </motion.span>
    </motion.button>
  );
}

// ─── Content (portal dropdown) ──────────────────────────────────────────────

export function SelectContent({ className, children }) {
  const { open, triggerRef, contentRef } = useSelect();
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (open && triggerRef.current) {
      setRect(triggerRef.current.getBoundingClientRect());
    }
  }, [open, triggerRef]);

  return createPortal(
    <AnimatePresence>
      {open && rect && (
        <motion.div
          ref={contentRef}
          initial={{ opacity: 0, y: -8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 420, damping: 32, mass: 0.8 }}
          style={{
            position: 'fixed',
            top: rect.bottom + 6,
            left: rect.left,
            width: Math.max(rect.width, 260),
            zIndex: 9999,
          }}
          className={twMerge(
            'rounded-xl border border-white/10',
            'bg-[rgba(10,12,20,0.97)] backdrop-blur-xl',
            'shadow-[0_8px_40px_rgba(0,0,0,0.6)]',
            'max-h-[400px] overflow-y-auto',
            'py-1.5',
            'select-scrollbar',
            className
          )}
          role="listbox"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

// ─── Group ───────────────────────────────────────────────────────────────────

export function SelectGroup({ children }) {
  return <div role="group">{children}</div>;
}

// ─── Label ───────────────────────────────────────────────────────────────────

export function SelectLabel({ children, className }) {
  return (
    <div
      className={twMerge(
        'px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500',
        className
      )}
    >
      {children}
    </div>
  );
}

// ─── Separator ───────────────────────────────────────────────────────────────

export function SelectSeparator({ className }) {
  return <div className={twMerge('my-1 mx-2 h-px bg-white/8', className)} />;
}

// ─── Item ────────────────────────────────────────────────────────────────────

export function SelectItem({ value, children, className }) {
  const { value: selectedValue, select } = useSelect();
  const isSelected = selectedValue === value;

  return (
    <motion.div
      role="option"
      aria-selected={isSelected}
      onClick={() => select(value)}
      whileHover={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
      className={twMerge(
        'flex items-center justify-between',
        'mx-1 px-3 py-2 rounded-lg',
        'text-sm cursor-pointer select-none outline-none',
        isSelected ? 'text-slate-100' : 'text-slate-300',
        'transition-colors duration-100',
        className
      )}
    >
      <span>{children}</span>
      <AnimatePresence>
        {isSelected && (
          <motion.span
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            className="text-blue-400 flex-shrink-0 ml-2"
          >
            <Check size={13} strokeWidth={2.5} />
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
